/**
 * <s-proof-video>: a customer video card. While on screen it plays a silent looping preview (motion allowed,
 * no data saver); the `--play` command restarts it from the beginning with sound and native controls. A command
 * button with data-src (and data-poster) plays that other video in this player instead ("More on camera"); when
 * it ends, the player returns to its own video. Only one card plays with sound at a time.
 */
class SProofVideo extends HTMLElement {
  #video = null;
  #observer = null;
  #ac = null;
  #quiet = false;
  #own = { src: '', poster: '' };

  connectedCallback() {
    this.#video = this.querySelector('video');
    if (!this.#video) return;
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#quiet = reduce || navigator.connection?.saveData === true;
    this.#own = { src: this.#video.querySelector('source')?.src || this.#video.currentSrc || '', poster: this.#video.poster };
    this.dataset.state = 'idle';

    this.addEventListener('command', (event) => {
      if (event.command !== '--play') return;
      const source = event.source instanceof HTMLElement ? event.source : null;
      this.#playStory(source?.dataset.src || '', source?.dataset.poster || '');
    }, opts);
    this.#video.addEventListener('ended', () => this.#toPreview(), opts);
    document.addEventListener('proof-video:story', (event) => {
      if (event.detail !== this && this.dataset.state === 'story') this.#toPreview();
    }, opts);

    if (this.#quiet || !('IntersectionObserver' in window)) return;
    this.#observer = new IntersectionObserver(([entry]) => {
      if (this.dataset.state === 'story') {
        if (!entry.isIntersecting) this.#video.pause();
        return;
      }
      if (entry.isIntersecting) this.#preview();
      else this.#video.pause();
    }, { threshold: 0.5 });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #setSource(src, poster) {
    const video = this.#video;
    const current = video.querySelector('source');
    if (!src || current?.src === src) return;
    video.pause();
    if (current) current.src = src;
    else video.src = src;
    if (poster) video.poster = poster;
    video.load();
  }

  #preview() {
    const video = this.#video;
    if (video.preload === 'none') video.preload = 'auto';
    video.muted = true;
    video.loop = true;
    video.play().then(() => { this.dataset.state = 'preview'; }).catch(() => {});
  }

  #playStory(src, poster) {
    const video = this.#video;
    document.dispatchEvent(new CustomEvent('proof-video:story', { detail: this }));
    const other = Boolean(src) && src !== this.#own.src;
    this.#setSource(other ? src : this.#own.src, other ? poster : this.#own.poster);
    this.toggleAttribute('data-other', other);
    this.dataset.state = 'story';
    video.preload = 'auto';
    video.loop = false;
    video.muted = false;
    video.controls = true;
    video.currentTime = 0;
    video.play().catch(() => {
      video.muted = true;
      video.play().catch(() => {});
    });
    video.focus({ preventScroll: true });
  }

  #toPreview() {
    const video = this.#video;
    video.controls = false;
    video.muted = true;
    video.loop = true;
    this.removeAttribute('data-other');
    this.#setSource(this.#own.src, this.#own.poster);
    this.dataset.state = 'idle';
    if (this.#quiet) {
      video.pause();
      return;
    }
    this.#preview();
  }
}

if (!customElements.get('s-proof-video')) customElements.define('s-proof-video', SProofVideo);
